// src/i18n.js - Internationalization configuration
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation & Common
      dashboard: "Dashboard",
      clients: "Clients",
      tasks: "Tasks",
      settings: "Settings",
      logout: "Logout",
      logOut: "Log Out",
      welcome: "Welcome",
      loading: "Loading...",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      add: "Add",
      search: "Search",
      filter: "Filter",
      export: "Export",
      import: "Import",
      founderMetrics: "Founder Metrics",
      commandCenter: "Command Center",
      aepWizard: "AEP Wizard",
      oepHub: "OEP Hub",
      automations: "Automations",
      login: "Login",
      signUp: "Sign Up",
      daysUntilAEP: "Days until AEP (Oct 15)",
      oepSeason: "OEP Season (Jan–Mar)",

      // Client Profile
      customerFor: "Customer for",
      lastContact: "Last contact",
      call: "Call",
      text: "Text",
      email: "Email",
      scheduleReview: "Schedule Review",
      createTask: "Create Task",
      editDetails: "Edit Details",

      // Client Details
      personalInformation: "Personal Information",
      insuranceHealthcare: "Insurance & Healthcare",
      documents: "Documents",
      notes: "Notes",
      fullName: "Full Name",
      dateOfBirth: "Date of Birth",
      phone: "Phone",
      address: "Address",
      preferredLanguage: "Preferred Language",
      effectiveDate: "Effective Date",
      carrier: "Carrier",
      plan: "Plan",
      primaryCare: "Primary Care",
      specialists: "Specialists",
      medications: "Medications",

      // Risk
      retentionRisk: "Retention Risk",
      highRisk: "High Risk",
      mediumRisk: "Medium Risk",
      lowRisk: "Low Risk",
      engaged: "Engaged",
      immediateAttention: "Immediate attention needed",
      monitorClosely: "Monitor closely",
      routineFollowUp: "Routine follow-up",
      clientEngaged: "Client is engaged",

      // Communications
      recentCommunication: "Recent Communication",
      viewAll: "View All",
      sendMessage: "Send Message",
      subject: "Subject",
      message: "Message",
      send: "Send",

      // Tasks
      upcomingTasks: "Upcoming Tasks",
      addTask: "Add Task",
      taskTitle: "Title",
      taskNotes: "Notes",
      priority: "Priority",
      dueDate: "Due Date",
      low: "Low",
      normal: "Normal",
      high: "High",

      // Documents
      addDocument: "Add Document",
      noDocuments: "No documents uploaded yet",
      clickToUpload: "Click \"Add Document\" to upload your first file",
      download: "Download",

      // Messages
      clientUpdated: "Client details updated successfully!",
      taskCreated: "Task created successfully!",
      emailSent: "Email sent successfully!",
      smsSent: "SMS sent successfully!",
      uploadFailed: "Upload failed",
      deleteFailed: "Delete failed",

      // Language Switcher
      language: "Language",
      english: "English",
      spanish: "Spanish",

      // Settings Page
      settings: "Settings",
      languagePreference: "Language Preference",
      chooseLanguage: "Choose your preferred language for the entire platform interface.",
      unitedStates: "United States",
      autoTranslationNote: "Automatic Client Communication Translation",
      autoTranslationDesc: "When you set a client's \"Preferred Language\" to Spanish, all SMS and emails you send to them will be automatically translated to Spanish - even if you type them in English!",
      languageSaved: "Language saved successfully!",

      // Client Profile Page - Additional
      retentionRisk: "Retention Risk",
      riskScore: "Risk Score",
      highRisk: "High Risk",
      mediumRisk: "Medium Risk",
      lowRisk: "Low Risk",
      engaged: "Engaged",
      immediateAttention: "Immediate attention needed",
      monitorClosely: "Monitor closely",
      routineFollowUp: "Routine follow-up",
      clientEngaged: "Client is engaged",
      upcomingTasks: "Upcoming Tasks",
      noTasksScheduled: "No tasks scheduled. Create your first task to get started!",
      createTaskFor: "Create Task for",
      title: "Title",
      notes: "Notes",
      priority: "Priority",
      dueDate: "Due Date",
      low: "Low",
      normal: "Normal",
      high: "High",
      createTask: "Create Task",

      // Dashboard
      totalClients: "Total Clients",
      highRiskClients: "High Risk Clients",
      tasksToday: "Tasks Today",
      recentActivity: "Recent Activity",
      riskDistribution: "Risk Distribution",

      // Common Actions
      viewProfile: "View Profile",
      viewDetails: "View Details",
      markComplete: "Mark Complete",
      close: "Close",

      // Clients Page
      allYourClients: "All your clients, prospects & contacts.",
      addClient: "Add Client",
      editClient: "Edit Client",
      deleteClient: "Delete Client",
      noClients: "No clients",
      name: "Name",
      carrier: "Carrier",
      plan: "Plan",
      created: "Created",
      actions: "Actions",
      searchNameEmailCarrier: "Search name, email, carrier…",
      moreFilters: "More Filters",
      columns: "Columns",
      sms: "SMS",
      pleaseConfirm: "Please confirm",
      deleteThisClient: "Delete this client?",
      saveFailed: "Save failed",
      deleteFailed: "Delete failed",
      totalRecords: "Total {{count}} record",
      totalRecords_plural: "Total {{count}} records",

      // Tasks Page
      allTasks: "All Tasks",
      myTasks: "My Tasks",
      myTasksOnly: "My Tasks only",
      complete: "Complete",
      clear: "Clear",
      all: "All",
      dueToday: "Due Today",
      upcoming: "Upcoming",
      overdue: "Overdue",
      completed: "Completed",
      searchTasks: "Search tasks…",
      newClientTask: "New Client Task",
      newTask: "New Task",
      linkToClient: "Link to Client (Optional)",
      noClientPersonalTask: "-- No Client (Personal Task) --",
      taskLinkedToClient: "This task will be linked to the selected client",
      personalTaskNotLinked: "This will be a personal task not linked to any client",
      due: "Due",
      clientLinkedTasksNote: "Client-linked tasks are completed on the Client Profile",
      toggleDone: "Toggle done",
      client: "Client",
      clientLinked: "Client-linked",
      myTask: "My Task",
      tasksCount: "{{count}} task",
      tasksCount_plural: "{{count}} tasks",
      noTasksMatch: "No tasks match your filter.",
      allTasksSummary: "All Tasks: {{all}} • My Tasks: {{mine}}",

      // Common Form Labels
      firstName: "First Name",
      lastName: "Last Name",
      address: "Address",
      status: "Status",

      // Dashboard Components
      welcomeBack: "Welcome back to Pro IRP!",
      totalClients: "Total Clients",
      retentionRate: "Retention Rate",
      churnedClients: "Churned Clients",
      newClients: "New Clients",
      tasksDue: "Tasks Due",
      thisMonth: "this month",
      days30: "30d",
      atRiskClients: "At-Risk Clients",
      noHighRiskClients: "No high-risk clients",
      unnamedClient: "Unnamed Client",
      risk: "Risk",
      noContactRecorded: "No contact recorded",
      noContactInMonths: "No contact in {{months}}+ months",
      noContactIn90Days: "No contact in 90+ days",
      noContactInDays: "No contact in {{days}} days",
      highRiskScore: "High risk score",

      // Calendar
      calendar: "Calendar",
      manageAppointments: "Manage your appointments and schedules",
      selectCalendar: "Select Calendar",
      refresh: "Refresh",
      connectGoogleCalendar: "Connect Google Calendar",
      connect: "Connect",
      newEvent: "New Event",
      googleCalendarConnected: "Google Calendar Connected - Events will sync automatically",
      connectCalendarToSync: "Connect your Google Calendar to sync events",
      eventTitle: "Event Title",
      start: "Start",
      end: "End",
      location: "Location",
      description: "Description",
      connectGoogleToCreate: "Connect Google Calendar to create events",
      creating: "Creating...",
      createEvent: "Create Event",
      selectCalendarToSync: "Select Calendar to Sync",
      chooseCalendarToSync: "Choose which Google Calendar you want to sync with Pro IRP:",
      primary: "Primary",
      access: "Access",
      noTitle: "(No title)",
      source: "Source",
      eventTitlePlaceholder: "Team meeting, Client call, etc.",
      locationPlaceholder: "Office, Zoom, Client site, etc.",
      descriptionPlaceholder: "Add notes, agenda, or details...",

      // Tasks (dashboard widget)
      noPendingTasks: "No pending tasks",
      noDueDate: "No due date",
      today: "Today",
      tomorrow: "Tomorrow",
      due: "Due",

      // Activity Feed
      recentActivityTitle: "Recent Activity",
      noRecentActivity: "No recent activity",
      justNow: "Just now",
      minutesAgo: "{{minutes}}m ago",
      hoursAgo: "{{hours}}h ago",
      daysAgo: "{{days}}d ago",
      sent: "Sent",
      received: "Received",

      // Alerts
      alerts: "Alerts",
      noAlerts: "No alerts - you're all caught up!",

      // Settings sections
      profileLogin: "Profile & Login",
      security: "Security",
      notifications: "Notifications",
      integrations: "Integrations",
      brandingTheme: "Branding & Theme",
      userManagement: "User Management",
      auditLogs: "Audit Logs",
      apiAccess: "API Access",
      dataExportImport: "Data Export / Import",
      compliancePrivacy: "Compliance & Privacy",
      dangerZone: "Danger Zone",

      // Settings Profile
      profileEditNote: "Profile editing coming soon. This is read-only for now.",
      name: "Name",
      role: "Role",
      phoneForCall: "Phone Number (for Click-to-Call)",
      phoneWillRing: "This phone will ring when you initiate a call to a client",
      saving: "Saving...",
      savePhoneNumber: "Save Phone Number",
      phoneNumberSaved: "Phone number saved!",
      otherProfileFieldsNote: "Other profile fields are read-only. Full profile editing coming soon.",

      // Settings Security
      updatePasswordAnd2FA: "Update your password and set up Two-Factor Authentication (2FA).",
      currentPassword: "Current Password",
      newPassword: "New Password",
      twoFactorAuth: "Two-Factor Authentication",
      enable2FA: "Enable 2FA",
      manageDevices: "Manage Devices",

      // Settings Notifications
      chooseNotifications: "Choose which notifications you want to receive.",
      smsReminders: "SMS Reminders",
      emailNotifications: "Email Notifications",
      inAppAlerts: "In-App Alerts",
      saveNotificationSettings: "Save Notification Settings",

      // Settings Branding
      customizeLookFeel: "Customize the look and feel of your account.",
      theme: "Theme",
      light: "Light",
      dark: "Dark",
      blue: "Blue",
      logo: "Logo",
      saveBranding: "Save Branding",

      // Settings User Management
      userManagementDesc: "Add or remove users, set permissions and assign roles (for agencies or FMOs).",
      admin: "Admin",
      agent: "Agent",
      remove: "Remove",
      addNewUser: "Add New User",

      // Settings Audit Logs
      auditLogsDesc: "See every key action performed in your account for compliance & transparency.",
      loggedIn: "Logged in",
      updatedClient: "Updated client",
      exportedClientData: "Exported client data",
      changedPassword: "Changed password",

      // Settings API
      apiAccessDesc: "Generate API tokens for third-party integrations.",
      regenerateToken: "Regenerate Token",
      apiTokenUsage: "Use API tokens to connect CRMs, dialers, or lead sources.",

      // Settings Data
      dataExportImportDesc: "Export or import your client and policy data.",
      exportData: "Export Data",
      importData: "Import Data",
      exportsEncrypted: "All exports are encrypted and can only be imported by an admin.",

      // Settings Compliance
      complianceSettings: "Review your compliance settings and privacy controls.",
      hipaaCompliance: "HIPAA Compliance",
      dataEncryption: "Data Encryption",
      enabled: "Enabled",
      active: "Active",
      viewPrivacyPolicy: "View Privacy Policy",

      // Settings Danger Zone
      dangerZoneDesc: "Delete your account or reset all data. This action is irreversible.",
      deleteAccount: "Delete Account",
      resetAllData: "Reset All Data",

      // Recent Communication
      noRecentCommunication: "No recent activity",

      // Login Page
      nextGenInsurance: "Next Generation Insurance Retention",
      emailLabel: "Email",
      passwordLabel: "Password",
      logIn: "Log In",
      forgotPassword: "Forgot password?",
      noAccount: "Don't have an account?",

      // Signup Page
      createYourAccount: "Create your account",
      signingUpAs: "I am signing up as:",
      agency: "Agency",
      agentRole: "Agent",
      manageAgency: "Manage your insurance agency and team",
      joinAgencyTeam: "Join your agency team",
      agencyName: "Agency Name",
      yourName: "Your Name",
      numberOfAgents: "Number of Agents",
      promoCode: "Promo Code (e.g., PILOT2025)",
      passwordMin8: "Password (min 8 characters)",
      confirmPassword: "Confirm Password",
      invitedToJoin: "You've been invited to join an agency!",
      emailMustMatch: "Email (must match invitation)",
      invitationToken: "Invitation Token",
      createAgencyAccount: "Create Agency Account",
      acceptInvitation: "Accept Invitation",
      alreadyHaveAccount: "Already have an account?",
      needPromoCode: "Need a promo code? Email",
      fmoPartnership: "FMO Partnership",
      fmoContact: "Are you an FMO looking to partner? Contact us at",

      // Forgot Password Page
      forgotPasswordTitle: "Forgot Password",
      resetCodeMessage: "We'll create a reset code for your account.",
      sendCode: "Send Code",

      // Client Profile Page
      healthConnectedSuccess: "Health data connected successfully! Your data will sync shortly.",
      healthConnectionFailed: "Health data connection failed",
      startsSoon: "Starts soon",
      noPhoneOnFile: "No phone number on file for this client. Please add a phone number first.",
      confirmCall: "Call {{name}} at {{phone}}?\n\nYour phone will ring first, then we'll connect you to the client.",
      callFailed: "Call failed",
      failedToInitiateCall: "Failed to initiate call. Please try again.",
      failedToUpdate: "Failed to update client",
      provideReasonLost: "Please provide a reason for losing this client.",
      lostCustomerPrefix: "--- Lost Customer",
      clientMarkedLost: "Client marked as lost.",
      reactivateConfirm: "Reactivate this client?",
      clientReactivated: "Client reactivated.",
      failedToReactivate: "Failed to reactivate",
      failedToCreateTask: "Failed to create task",
      activeStatus: "Active",
      prospectStatus: "Prospect",
      lostStatus: "Lost",
      healthConnected: "Health Connected",
      syncing: "Syncing...",
      sync: "Sync",
      disconnectConfirm: "Disconnect?",
      yes: "Yes",
      no: "No",
      connectHealthData: "Connect Health Data",
      markAsLost: "Mark as Lost",
      reactivate: "Reactivate",
      markClientLostTitle: "Mark Client as Lost?",
      markClientLostConfirm: "Are you sure you want to mark {{name}} as lost?",
      canReactivateLater: "This action will change the client's status. You can reactivate them later if needed.",
      reasonForLosing: "Reason for losing this client",
      reasonPlaceholder: "e.g., Switched to competitor, moved out of area, cost concerns...",
      yesMarkAsLost: "Yes, Mark as Lost",
      callToDiscussPlaceholder: "Call to discuss policy renewal...",
      additionalDetailsPlaceholder: "Additional details...",

      // Client Detail Card
      healthDataConnected: "Health Data Connected",
      planType: "Plan Type",
      lastSynced: "Last Synced",
      prescriptions: "Prescriptions",
      medicationsCount: "({{count}} medications)",
      noPrescriptionsFound: "No prescriptions found. Click Sync to pull data.",
      connectMedicareBlueButton: "Connect Medicare Blue Button to import prescriptions",
      medication: "Medication",
      lastFilled: "Last Filled",
      qty: "Qty",
      days: "Days",
      cost: "Cost",
      pharmacy: "Pharmacy",
      soa: "SOA",
      ptc: "PTC",
      enrollment: "Enrollment",
      other: "Other",
      uploading: "Uploading…",
      document: "Document",

      // Client Edit Modal
      editClientDetails: "Edit Client Details",
      autoFormatsPhone: "Auto-formats to +1 (XXX) XXX-XXXX",
      city: "City",
      state: "State",
      zip: "Zip",
      autoTranslatesSmsEmail: "(Auto-translates SMS/Email)",
      englishFlag: "English",
      spanishFlag: "Spanish (Español)",
      soaOnFile: "SOA On File (true/false)",
      soaSigned: "SOA Signed (YYYY-MM-DD)",
      ptcOnFile: "Permission to Contact On File (true/false)",
      ptcSigned: "PTC Signed (YYYY-MM-DD)",
      enrollmentOnFile: "Enrollment Form On File (true/false)",

      // Client Form Full
      phonePlaceholder: "(555) 123-4567",
      emailPlaceholderForm: "name@example.com",
      addressPlaceholder: "123 Main St, City, ST 12345",
      dob: "DOB",
      carrierPlaceholder: "UHC / Humana / Aetna…",
      planNamePlaceholder: "Plan name / code",
      primaryCarePlaceholder: "Dr. Bob Smith",
      specialistsPlaceholder: "Dr. Jane Specialist, Dr. Tim Ortho",
      medicationsPlaceholder: "Aspirin, Lisinopril",
      soaOnFileLabel: "SOA on file",
      soaSignedLabel: "SOA signed (date)",
      ptcOnFileLabel: "Permission to Contact on file",
      ptcSignedLabel: "PTC signed (date)",
      enrollmentOnFileLabel: "Enrollment form on file",
      anyDetailsPlaceholder: "Any details…",
      autoTranslateNote: "When you send SMS or email to this client, it will automatically translate to their preferred language.",

      // Outreach Log
      enterSubjectOrNote: "Enter a subject or note.",
      failedToSaveOutreach: "Failed to save outreach.",
      deleteEntryConfirm: "Delete this entry?",
      outreachLog: "Outreach Log",
      outreachLogSubtitle: "Calls, texts, emails — all in one place.",
      subjectOptional: "Subject (optional)",
      addEntry: "Add Entry",
      noOutreachYet: "No outreach yet.",
      when: "When",
      type: "Type",
      note: "Note",

      // Comms Drawer
      allCommunication: "All Communication",
      texts: "Texts",
      emails: "Emails",
      calls: "Calls",
      appointments: "Appointments",
      noEntriesYet: "No entries yet.",
      clientToYou: "Client → You",
      youToClient: "You → Client",
      subjectLabel: "Subject:",
      previewLabel: "Preview:",
      duration: "Duration",

      // Message Thread
      smsText: "SMS Text",
      smsSentSuccess: "SMS sent successfully!",
      emailSentSuccess: "Email sent successfully!",
      failedToSend: "Failed to send",
      unread: "unread",

      // Integrations Section
      integrationsTitle: "Integrations",
      integrationsDesc: "Connect your email and calendar to send professional emails from your account and sync appointments.",
      googleAccount: "Google Account",
      gmailCalendar: "Gmail + Google Calendar",
      connected: "Connected",
      notConnected: "Not Connected",
      emailSending: "Email Sending",
      sendEmailsFromGmail: "Send emails to clients from your Gmail address",
      calendarSync: "Calendar Sync",
      autoSyncAppointments: "Automatically sync appointments and tasks",
      connectedOn: "Connected on {{date}}",
      disconnect: "Disconnect",
      connectGoogleAccount: "Connect Google Account",
      microsoftAccount: "Microsoft Account",
      outlookM365: "Outlook + Microsoft 365",
      sendEmailsFromOutlook: "Send emails to clients from your Outlook address",
      connectMicrosoftAccount: "Connect Microsoft Account",
      whyConnectEmail: "Why connect your email?",
      emailBenefit1: "Emails come from YOUR address (more trusted by clients)",
      emailBenefit2: "Replies go directly to your inbox",
      emailBenefit3: "Automatic Spanish translation for client communications",
      emailBenefit4: "Professional appearance & better deliverability",

      // Duration text
      day: "day",
      days: "days",
      month: "month",
      months: "months",
      year: "year",
      years: "years",

      // Founder Dashboard
      founderDashboard: "Founder Dashboard",
      trackPilotPerformance: "Track pilot program performance, agent adoption, and system metrics",
      totalAgents: "Total Agents",
      activeAgentsCount: "{{active}} active, {{inactive}} inactive",
      communications: "Communications",
      acrossAllChannels: "Across all channels",
      taskCompletion: "Task Completion",
      tasksOfTotal: "{{completed}} of {{total}} tasks",
      agentPerformance: "Agent Performance",
      topPerformers: "Top Performers",
      communicationActivity: "Communication Activity",
      retentionByAgent: "Retention by Agent",
      systemAdoption: "System Adoption",
      csvImportsPerformed: "CSV Imports Performed",
      bulkClientUploads: "Bulk client uploads by agents",
      avgLoginsPerAgent: "Avg Logins per Agent",
      activeEngagement7Days: "Active engagement over 7 days",
      noAgentData: "No agent data available",
      noPerformersData: "No performers data available",
      noCommunicationData: "No communication data available",
      noRetentionData: "No retention data available",
      loadingFounderMetrics: "Loading founder metrics...",
      accessDeniedFounder: "Access denied - Founder metrics are only available to FMO/Admin users",
      failedToFetchFounder: "Failed to fetch founder metrics",
      comms: "comms",
      retention: "retention",
      appointment: "Appointment",

      // Retention Charts
      retentionTrend: "Retention Trend",
      churnBreakdown: "Churn Breakdown",
      retentionTrendDemo: "Retention Trend (Demo)",
      churnBreakdownDemo: "Churn Breakdown (Demo)",
      sampleDataOnly: "Sample data – real analytics coming soon",

      // Quick Lookup
      quickClientLookup: "Quick Client Lookup",
      searchByName: "Search by name...",
      noClientsFound: "No clients found",

      // Churn Modal
      logClientChurn: "Log Client Churn",
      hasLeftPlan: "{{name}} has left the plan",
      whenDidTheyLeave: "When did they leave?",
      primaryReasonForLeaving: "Primary Reason for Leaving",
      newCarrier: "New Carrier",
      newPlan: "New Plan",
      couldThisBeenPrevented: "Could this have been prevented?",
      yesPreventable: "Yes, preventable",
      noUnavoidable: "No, unavoidable",
      whatCouldPrevented: "What could have prevented this?",
      earlyOutreach: "Earlier outreach/contact",
      betterPlanMatch: "Better plan recommendation",
      costAssistance: "Help with cost/LIS enrollment",
      providerNetwork: "Network/provider issue resolution",
      education: "Better plan education",
      relationshipBuilding: "Stronger relationship building",
      warningSignsQuestion: "What warning signs did you notice? (helps train the AI)",
      additionalNotes: "Additional Notes",
      anyContextWhy: "Any additional context about why they left...",
      logChurn: "Log Churn",
      logging: "Logging...",
      pleaseSelectPrimaryReason: "Please select a primary reason",
      costIssues: "Cost Issues",
      coverageIssues: "Coverage Issues",
      serviceIssues: "Service Issues",
      lifeEvents: "Life Events",
      competitive: "Competitive",
      complainedAboutCopays: "e.g., Complained about copays",
      egHumana: "e.g., Humana",
      planName: "Plan name",
      maMedicareAdvantage: "MA (Medicare Advantage)",
      maPd: "MA-PD",
      dsnp: "DSNP",
      pdpPartD: "PDP (Part D)",
      medigap: "Medigap",
      unknown: "Unknown",

      // Automations Page
      automationsWorkflows: "Automations & Workflows",
      newWorkflow: "New Workflow",
      help: "Help",
      myAutomations: "My Automations",
      birthdayTextEmail: "Birthday Text/Email",
      sendsHappyBirthday: "Sends happy birthday message to clients",
      policyAnniversary: "Policy Anniversary",
      annualCheckInReminder: "Annual check-in reminder on policy date",
      monthlyNewsletter: "Monthly Newsletter",
      sendsMonthlyUpdates: "Sends monthly health tips newsletter",
      aepReminder: "AEP Reminder",
      octAepReminder: "Oct 1 AEP reminder for eligible clients",
      oepFollowUp: "OEP Follow-up",
      janOepCheckin: "Jan-Mar OEP check-in sequence",
      renewalReminder: "Renewal Reminder",
      daysBeforeRenewal: "30 days before renewal notice",
      activityFeed: "Activity Feed",
      analytics: "Analytics",
      automationsSent: "Automations Sent",
      openRate: "Open Rate",
      clickRate: "Click Rate",
      clientsReached: "Clients Reached",
      templatesTitle: "Templates",
      createNew: "Create New",
      personalization: "Personalization",
      firstName: "First Name",
      lastName: "Last Name",
      planTypeVar: "Plan Type",
      birthdayTemplate: "Birthday",
      anniversaryTemplate: "Anniversary",
      aepTemplate: "AEP",
      oepTemplate: "OEP",
      proIrpAssistant: "Pro IRP Assistant",
      askAboutAutomations: "Ask about automations, best practices, or get help setting up workflows...",
      typeQuestion: "Type a question...",
      automationsComingSoon: "Automations engine coming soon! Set up birthday wishes, anniversary reminders, AEP/OEP outreach, and more.",

      // AEP Wizard
      aepWizardTitle: "AEP Wizard",
      aepStartsIn: "AEP starts in:",
      daysLabel: "days",
      hoursLabel: "hours",
      minsLabel: "mins",
      eventMode: "Event Mode",
      letsWinAEP: "Let's Win AEP",
      sendPreAepBlast: "Send Pre-AEP Blast",
      openBooking: "Open Booking",
      createCallList: "Create Call List",
      outreachAutomations: "Outreach Automations",
      templates: "Templates",
      preAepCheckin: "Pre-AEP Check-in",
      aepOpenEnrollment: "AEP Open Enrollment",
      planComparison: "Plan Comparison",
      benefitsReview: "Benefits Review",
      countdownList: "Countdown List",
      clientsToContactBeforeAep: "Clients to contact before AEP",
      allClientsContacted: "All clients contacted!",
      contacted: "Contacted",
      needsContact: "Needs Contact",
      noClientsToContact: "No clients to contact",
      aepGoals: "AEP Goals",
      setGoal: "Set Goal",
      enrollments: "Enrollments",
      appointments: "Appointments",
      currentPeriod: "Current Period",
      preAep: "Pre-AEP",
      aepActive: "AEP Active",
      postAep: "Post-AEP",
      daysUntilAepEnds: "days until AEP ends",
      aepHasEnded: "AEP has ended",
      quickActions: "Quick Actions",
      scheduleAepEvent: "Schedule AEP Event",
      sendMassEmail: "Send Mass Email",
      downloadMaterials: "Download Materials",
      viewReports: "View Reports",
      aepTips: "AEP Tips",
      tipContactHighRisk: "Contact high-risk clients first",
      tipReviewPlanChanges: "Review plan changes for each carrier",
      tipPrepareComparisons: "Prepare plan comparison sheets",
      tipScheduleFollowups: "Schedule follow-ups for undecided clients",
      aepResources: "AEP Resources",
      carrierUpdates: "Carrier Updates",
      viewLatestChanges: "View latest plan changes",
      complianceGuide: "Compliance Guide",
      stayCompliant: "Stay compliant during AEP",
      marketingMaterials: "Marketing Materials",
      downloadFlyers: "Download flyers & brochures",
      trainingVideos: "Training Videos",
      watchTutorials: "Watch AEP tutorials",
      recentAepActivity: "Recent AEP Activity",
      noRecentAepActivity: "No recent AEP activity",
      scheduledAepEvent: "Scheduled AEP event",
      sentAepEmail: "Sent AEP email to",
      completedAepCall: "Completed AEP call with",

      // OEP Hub
      oepRetentionHub: "OEP Retention Hub",
      retentionMode: "Retention Mode",
      keepEveryWin: "Keep Every Win",
      sendJan1Blast: "Send Jan 1 Blast",
      oepCohort: "OEP Cohort",
      followUpsSent: "Follow-ups Sent",
      churn: "Churn",
      oepAutomations: "OEP Automations",
      jan1Welcome: "Jan 1 Welcome",
      benefitReminder: "Benefit Reminder",
      satisfactionCheck: "Satisfaction Check",
      oepDeadline: "OEP Deadline",
      retentionList: "Retention List",
      clientsToRetainDuringOep: "Clients to retain during OEP",
      allClientsRetained: "All clients retained!",
      retained: "Retained",
      atRisk: "At Risk",
      noClientsAtRisk: "No clients at risk",
      oepGoals: "OEP Goals",
      retentionGoal: "Retention Goal",
      checkIns: "Check-ins",
      currentOepPeriod: "Current OEP Period",
      oepActive: "OEP Active",
      preOep: "Pre-OEP",
      postOep: "Post-OEP",
      daysUntilOepEnds: "days until OEP ends",
      oepHasEnded: "OEP has ended",
      oepQuickActions: "Quick Actions",
      scheduleCheckIn: "Schedule Check-in",
      sendRetentionEmail: "Send Retention Email",
      reviewAtRisk: "Review At-Risk",
      oepTips: "OEP Tips",
      tipReachOutEarly: "Reach out to at-risk clients early",
      tipAddressConcerns: "Address concerns before they switch",
      tipHighlightBenefits: "Highlight benefits they may have forgotten",
      tipDocumentInteractions: "Document all client interactions",
      oepResources: "OEP Resources",
      retentionScripts: "Retention Scripts",
      provenTalkingPoints: "Proven talking points",
      benefitHighlights: "Benefit Highlights",
      keyBenefitsbyPlan: "Key benefits by plan",
      competitorInfo: "Competitor Info",
      knowCompetition: "Know the competition",
      recentOepActivity: "Recent OEP Activity",
      noRecentOepActivity: "No recent OEP activity",
      completedRetentionCall: "Completed retention call",
      sentFollowUpTo: "Sent follow-up to",
      scheduledCheckInWith: "Scheduled check-in with",

      // Additional Automations strings
      active: "Active",
      inactive: "Inactive",
      delivered: "Delivered",
      opened: "Opened",
      failed: "Failed",
      resend: "Resend",
      exportActivityCsv: "Export Activity (CSV)",
      searchTemplates: "Search templates...",
      preview: "Preview",
      insert: "Insert",
      testSend: "Test Send",
      insertIntoWorkflow: "Insert into Workflow",
      createNewWorkflow: "Create New Workflow",
      workflowName: "Name",
      triggers: "Triggers",
      actionsLabel: "Action(s)",
      actionPlaceholder: "e.g., Send email, text, assign task (use {ClientName}, {AgentName} etc. for personalization)",
      createWorkflow: "Create Workflow",
      needInspiration: "Need inspiration? Check the Templates panel for ready-to-use messages!",
      askMeAnything: "Ask me anything...",
      automationHelpTips: "Automation Help & Tips",
      toggleAutomations: "Toggle automations on/off anytime with the switches.",
      addWorkflows: "Add workflows for custom triggers, messages, or multi-step sequences.",
      usePersonalizationTags: "Use personalization tags in all templates for unique, personalized outreach.",
      previewAndTestSend: "Preview and test send every message before you go live.",
      aiAssistantCanHelp: "The AI Assistant can help you draft, explain, or optimize any message or automation.",
      checkActivityFeed: "Check the Activity Feed for delivery status and quick resend if anything fails.",
      seeStatsInAnalytics: "See your stats and reply rates in the Analytics section below.",
      clientName: "Client Name",
      agentName: "Agent Name",
      agentPhone: "Agent Phone",
      policyYear: "Policy Year",
      replyRate: "Reply Rate",
      bounces: "Bounces",
      failedSends: "Failed Sends",
      askAi: "Ask AI",
      personalizedForEachClient: "Use these tags in any message—each will be replaced automatically for every client.",

      // Additional AEP Wizard strings
      loadingPlaybook: "Loading the playbook...",
      enterWizard: "Enter Wizard",
      focusedOutreach: "Focused outreach. Faster booking. Clear priorities. Make every touch count.",
      preAepOutreach: "Pre-AEP Outreach",
      bookingGoal: "Booking Goal",
      riskCoverage: "Risk Coverage",
      repliesHandled: "Replies Handled",
      preAep60Days: "Pre-AEP: 60 days",
      preAep30Days: "Pre-AEP: 30 days",
      preAep14Days: "Pre-AEP: 14 days",
      preAep7Days: "Pre-AEP: 7 days",
      preAep3Days: "Pre-AEP: 3 days",
      preAep1Day: "Pre-AEP: 1 day",
      anocExplainer: "ANOC Explainer",
      bookingNudges: "Booking Nudges",
      voicemailDropUi: "Voicemail Drop (UI only)",
      requireApproval: "Require Approval",
      searchTemplatesPlaceholder: "Search templates...",
      featured: "Featured",
      aepWizardHelper: "AEP Wizard Helper",
      askMeToDraft: "Ask me to draft, optimize, or summarize…",
      addContact: "Add Contact",
      sendDrip: "Send Drip",
      noContactsYet: "No contacts yet. Click Add Contact to start your Countdown List.",
      addCountdownContact: "Add Countdown Contact",
      editCountdownContact: "Edit Countdown Contact",
      zip: "ZIP",
      county: "County",
      dob: "DOB",
      languageLabel: "Language",
      sourceLabel: "Source",
      statusLabel: "Status",
      permissionToContact: "Permission to Contact",
      monthlyNewsletterLabel: "Monthly Newsletter",
      outreachPlan: "Outreach Plan",
      twoMonths: "2 Months",
      oneMonth: "1 Month",
      twoWeeks: "2 Weeks",
      oneWeek: "1 Week",
      aepLive: "AEP Live",
      saveChanges: "Save Changes",
      usingAepWizard: "Using AEP Wizard",
      useQuickActions: "Use Quick Actions to send pre-AEP blasts, create call lists, and open your scheduler.",
      automationsControlDrips: "Automations control your pre-AEP drips (60/30/14/7/3/1 days), ANOC explainers and booking nudges.",
      templatesPersonalize: "Templates are ready to personalize with merge tags.",
      countdownListStores: "Countdown List stores year-round prospects who must wait until AEP—add, track status, and drip them automatically.",
      aiHelperCanDraft: "The AI Helper can draft copy, subject lines, and compliance-safer phrasing (demo).",
      personalizationExample: "Personalization Example",
      mergeTags: "Merge Tags",
      openAiHelper: "Open AI Helper",
      exportCsv: "Export (CSV)",
      newStatus: "New",
      warmStatus: "Warm",
      scheduledStatus: "Scheduled",
      enrolledStatus: "Enrolled",
      notInterestedStatus: "Not Interested",
      eventSource: "Event",
      referralSource: "Referral",
      inboundSource: "Inbound",
      otherSource: "Other",
      englishLang: "English",
      spanishLang: "Spanish",
      otherLang: "Other",

      // Additional OEP Hub strings
      oepSeasonActive: "Active",
      oepSeasonUpcoming: "Upcoming",
      stopChurnBeforeStarts: "Welcome. Check-in. Lock the relationship. Stop churn before it starts.",
      openServiceDesk: "Open Service Desk",
      referralToolkit: "Referral Toolkit",
      jan1Congrats: "Jan 1: Congrats / Cards Arrived?",
      feb1FirstMonth: "Feb 1: First Full Month Check-in",
      mar1FollowUpReferrals: "Mar 1: Follow-up & Referrals",
      monthlyNewslettersCohort: "Monthly Newsletters for Cohort",
      oepRetentionHelper: "OEP Retention Helper",
      askForCheckin: "Ask for a Feb 1 check-in, churn save, or referral text…",
      addOepClient: "Add OEP Client",
      editOepClient: "Edit OEP Client",
      effectiveDateLabel: "Effective Date",
      firstTimeWithAgent: "First Time with Agent",
      sendJan1: "Send Jan 1",
      sendFeb1: "Send Feb 1",
      sendMar1: "Send Mar 1",
      noClientsInOepCohort: "No clients in the OEP cohort yet. Add one or sync from your Clients list.",
      cohortWindowNote: "Clients added here are filtered into the OEP cohort if their effective date is between {{start}} and {{end}} and it's their first time with the agent.",
      activeStatus: "Active",
      switchedStatus: "Switched",
      cancelledStatus: "Cancelled",
      yesLabel: "Yes",
      noLabel: "No"
    }
  },
  es: {
    translation: {
      // Navegación y Común
      dashboard: "Panel",
      clients: "Clientes",
      tasks: "Tareas",
      settings: "Configuración",
      logout: "Cerrar Sesión",
      logOut: "Cerrar Sesión",
      welcome: "Bienvenido",
      loading: "Cargando...",
      save: "Guardar",
      cancel: "Cancelar",
      delete: "Eliminar",
      edit: "Editar",
      add: "Agregar",
      search: "Buscar",
      filter: "Filtrar",
      export: "Exportar",
      import: "Importar",
      founderMetrics: "Métricas de Fundador",
      commandCenter: "Centro de Comando",
      aepWizard: "Asistente AEP",
      oepHub: "Centro OEP",
      automations: "Automatizaciones",
      login: "Iniciar Sesión",
      signUp: "Registrarse",
      daysUntilAEP: "Días hasta AEP (15 Oct)",
      oepSeason: "Temporada OEP (Ene–Mar)",

      // Perfil del Cliente
      customerFor: "Cliente desde hace",
      lastContact: "Último contacto",
      call: "Llamar",
      text: "Mensaje",
      email: "Correo",
      scheduleReview: "Programar Revisión",
      createTask: "Crear Tarea",
      editDetails: "Editar Detalles",

      // Detalles del Cliente
      personalInformation: "Información Personal",
      insuranceHealthcare: "Seguro y Atención Médica",
      documents: "Documentos",
      notes: "Notas",
      fullName: "Nombre Completo",
      dateOfBirth: "Fecha de Nacimiento",
      phone: "Teléfono",
      address: "Dirección",
      preferredLanguage: "Idioma Preferido",
      effectiveDate: "Fecha de Vigencia",
      carrier: "Aseguradora",
      plan: "Plan",
      primaryCare: "Médico de Atención Primaria",
      specialists: "Especialistas",
      medications: "Medicamentos",

      // Riesgo
      retentionRisk: "Riesgo de Retención",
      highRisk: "Alto Riesgo",
      mediumRisk: "Riesgo Medio",
      lowRisk: "Bajo Riesgo",
      engaged: "Comprometido",
      immediateAttention: "Requiere atención inmediata",
      monitorClosely: "Monitorear de cerca",
      routineFollowUp: "Seguimiento de rutina",
      clientEngaged: "Cliente está comprometido",

      // Comunicaciones
      recentCommunication: "Comunicación Reciente",
      viewAll: "Ver Todo",
      sendMessage: "Enviar Mensaje",
      subject: "Asunto",
      message: "Mensaje",
      send: "Enviar",

      // Tareas
      upcomingTasks: "Próximas Tareas",
      addTask: "Agregar Tarea",
      taskTitle: "Título",
      taskNotes: "Notas",
      priority: "Prioridad",
      dueDate: "Fecha de Vencimiento",
      low: "Baja",
      normal: "Normal",
      high: "Alta",

      // Documentos
      addDocument: "Agregar Documento",
      noDocuments: "No hay documentos subidos todavía",
      clickToUpload: "Haga clic en \"Agregar Documento\" para subir su primer archivo",
      download: "Descargar",

      // Mensajes
      clientUpdated: "¡Detalles del cliente actualizados exitosamente!",
      taskCreated: "¡Tarea creada exitosamente!",
      emailSent: "¡Correo enviado exitosamente!",
      smsSent: "¡SMS enviado exitosamente!",
      uploadFailed: "Error al subir",
      deleteFailed: "Error al eliminar",

      // Selector de Idioma
      language: "Idioma",
      english: "Inglés",
      spanish: "Español",

      // Página de Configuración
      settings: "Configuración",
      languagePreference: "Preferencia de Idioma",
      chooseLanguage: "Elija su idioma preferido para toda la interfaz de la plataforma.",
      unitedStates: "Estados Unidos",
      autoTranslationNote: "Traducción Automática de Comunicaciones con Clientes",
      autoTranslationDesc: "Cuando establece el \"Idioma Preferido\" de un cliente en Español, todos los SMS y correos electrónicos que les envíe se traducirán automáticamente al español, ¡incluso si los escribe en inglés!",
      languageSaved: "¡Idioma guardado exitosamente!",

      // Página de Perfil del Cliente - Adicional
      retentionRisk: "Riesgo de Retención",
      riskScore: "Puntuación de Riesgo",
      highRisk: "Alto Riesgo",
      mediumRisk: "Riesgo Medio",
      lowRisk: "Bajo Riesgo",
      engaged: "Comprometido",
      immediateAttention: "Requiere atención inmediata",
      monitorClosely: "Monitorear de cerca",
      routineFollowUp: "Seguimiento de rutina",
      clientEngaged: "Cliente está comprometido",
      upcomingTasks: "Próximas Tareas",
      noTasksScheduled: "No hay tareas programadas. ¡Crea tu primera tarea para comenzar!",
      createTaskFor: "Crear Tarea para",
      title: "Título",
      notes: "Notas",
      priority: "Prioridad",
      dueDate: "Fecha de Vencimiento",
      low: "Baja",
      normal: "Normal",
      high: "Alta",
      createTask: "Crear Tarea",

      // Panel de Control
      totalClients: "Total de Clientes",
      highRiskClients: "Clientes de Alto Riesgo",
      tasksToday: "Tareas de Hoy",
      recentActivity: "Actividad Reciente",
      riskDistribution: "Distribución de Riesgo",

      // Acciones Comunes
      viewProfile: "Ver Perfil",
      viewDetails: "Ver Detalles",
      markComplete: "Marcar Completo",
      close: "Cerrar",

      // Página de Clientes
      allYourClients: "Todos sus clientes, prospectos y contactos.",
      addClient: "Agregar Cliente",
      editClient: "Editar Cliente",
      deleteClient: "Eliminar Cliente",
      noClients: "Sin clientes",
      name: "Nombre",
      carrier: "Aseguradora",
      plan: "Plan",
      created: "Creado",
      actions: "Acciones",
      searchNameEmailCarrier: "Buscar nombre, correo, aseguradora…",
      moreFilters: "Más Filtros",
      columns: "Columnas",
      sms: "SMS",
      pleaseConfirm: "Por favor confirme",
      deleteThisClient: "¿Eliminar este cliente?",
      saveFailed: "Error al guardar",
      deleteFailed: "Error al eliminar",
      totalRecords: "Total {{count}} registro",
      totalRecords_plural: "Total {{count}} registros",

      // Página de Tareas
      allTasks: "Todas las Tareas",
      myTasks: "Mis Tareas",
      myTasksOnly: "Solo Mis Tareas",
      complete: "Completar",
      clear: "Limpiar",
      all: "Todas",
      dueToday: "Hoy",
      upcoming: "Próximas",
      overdue: "Atrasadas",
      completed: "Completadas",
      searchTasks: "Buscar tareas…",
      newClientTask: "Nueva Tarea de Cliente",
      newTask: "Nueva Tarea",
      linkToClient: "Vincular a Cliente (Opcional)",
      noClientPersonalTask: "-- Sin Cliente (Tarea Personal) --",
      taskLinkedToClient: "Esta tarea se vinculará al cliente seleccionado",
      personalTaskNotLinked: "Esta será una tarea personal no vinculada a ningún cliente",
      due: "Vencimiento",
      clientLinkedTasksNote: "Las tareas vinculadas a clientes se completan en el Perfil del Cliente",
      toggleDone: "Marcar como completada",
      client: "Cliente",
      clientLinked: "Vinculada a cliente",
      myTask: "Mi Tarea",
      tasksCount: "{{count}} tarea",
      tasksCount_plural: "{{count}} tareas",
      noTasksMatch: "No hay tareas que coincidan con su filtro.",
      allTasksSummary: "Todas las Tareas: {{all}} • Mis Tareas: {{mine}}",

      // Etiquetas Comunes de Formulario
      firstName: "Nombre",
      lastName: "Apellido",
      address: "Dirección",
      status: "Estado",

      // Componentes del Panel
      welcomeBack: "¡Bienvenido de nuevo a Pro IRP!",
      totalClients: "Total de Clientes",
      retentionRate: "Tasa de Retención",
      churnedClients: "Clientes Perdidos",
      newClients: "Nuevos Clientes",
      tasksDue: "Tareas Pendientes",
      thisMonth: "este mes",
      days30: "30d",
      atRiskClients: "Clientes en Riesgo",
      noHighRiskClients: "No hay clientes de alto riesgo",
      unnamedClient: "Cliente sin Nombre",
      risk: "Riesgo",
      noContactRecorded: "No hay contacto registrado",
      noContactInMonths: "Sin contacto en {{months}}+ meses",
      noContactIn90Days: "Sin contacto en 90+ días",
      noContactInDays: "Sin contacto en {{days}} días",
      highRiskScore: "Puntuación de alto riesgo",

      // Calendario
      calendar: "Calendario",
      manageAppointments: "Gestiona tus citas y horarios",
      selectCalendar: "Seleccionar Calendario",
      refresh: "Actualizar",
      connectGoogleCalendar: "Conectar Google Calendar",
      connect: "Conectar",
      newEvent: "Nuevo Evento",
      googleCalendarConnected: "Google Calendar Conectado - Los eventos se sincronizarán automáticamente",
      connectCalendarToSync: "Conecta tu Google Calendar para sincronizar eventos",
      eventTitle: "Título del Evento",
      start: "Inicio",
      end: "Fin",
      location: "Ubicación",
      description: "Descripción",
      connectGoogleToCreate: "Conecta Google Calendar para crear eventos",
      creating: "Creando...",
      createEvent: "Crear Evento",
      selectCalendarToSync: "Seleccionar Calendario para Sincronizar",
      chooseCalendarToSync: "Elige qué Google Calendar quieres sincronizar con Pro IRP:",
      primary: "Principal",
      access: "Acceso",
      noTitle: "(Sin título)",
      source: "Fuente",
      eventTitlePlaceholder: "Reunión de equipo, Llamada con cliente, etc.",
      locationPlaceholder: "Oficina, Zoom, Sitio del cliente, etc.",
      descriptionPlaceholder: "Añade notas, agenda o detalles...",

      // Tareas (widget del panel)
      noPendingTasks: "No hay tareas pendientes",
      noDueDate: "Sin fecha límite",
      today: "Hoy",
      tomorrow: "Mañana",

      // Feed de Actividad
      recentActivityTitle: "Actividad Reciente",
      noRecentActivity: "No hay actividad reciente",
      justNow: "Ahora mismo",
      minutesAgo: "hace {{minutes}}m",
      hoursAgo: "hace {{hours}}h",
      daysAgo: "hace {{days}}d",
      sent: "Enviado",
      received: "Recibido",

      // Alertas
      alerts: "Alertas",
      noAlerts: "Sin alertas - ¡todo al día!",

      // Secciones de Configuración
      profileLogin: "Perfil e Inicio de Sesión",
      security: "Seguridad",
      notifications: "Notificaciones",
      integrations: "Integraciones",
      brandingTheme: "Marca y Tema",
      userManagement: "Gestión de Usuarios",
      auditLogs: "Registros de Auditoría",
      apiAccess: "Acceso API",
      dataExportImport: "Exportar / Importar Datos",
      compliancePrivacy: "Cumplimiento y Privacidad",
      dangerZone: "Zona de Peligro",

      // Configuración Perfil
      profileEditNote: "Edición de perfil próximamente. Solo lectura por ahora.",
      name: "Nombre",
      role: "Rol",
      phoneForCall: "Número de Teléfono (para Llamar con un Clic)",
      phoneWillRing: "Este teléfono sonará cuando inicies una llamada a un cliente",
      saving: "Guardando...",
      savePhoneNumber: "Guardar Número de Teléfono",
      phoneNumberSaved: "¡Número de teléfono guardado!",
      otherProfileFieldsNote: "Otros campos del perfil son de solo lectura. Edición completa próximamente.",

      // Configuración Seguridad
      updatePasswordAnd2FA: "Actualiza tu contraseña y configura la Autenticación de Dos Factores (2FA).",
      currentPassword: "Contraseña Actual",
      newPassword: "Nueva Contraseña",
      twoFactorAuth: "Autenticación de Dos Factores",
      enable2FA: "Habilitar 2FA",
      manageDevices: "Administrar Dispositivos",

      // Configuración Notificaciones
      chooseNotifications: "Elige qué notificaciones deseas recibir.",
      smsReminders: "Recordatorios SMS",
      emailNotifications: "Notificaciones por Correo",
      inAppAlerts: "Alertas en la App",
      saveNotificationSettings: "Guardar Configuración de Notificaciones",

      // Configuración Marca
      customizeLookFeel: "Personaliza la apariencia de tu cuenta.",
      theme: "Tema",
      light: "Claro",
      dark: "Oscuro",
      blue: "Azul",
      logo: "Logo",
      saveBranding: "Guardar Marca",

      // Configuración Gestión de Usuarios
      userManagementDesc: "Agrega o elimina usuarios, establece permisos y asigna roles (para agencias o FMOs).",
      admin: "Administrador",
      agent: "Agente",
      remove: "Eliminar",
      addNewUser: "Agregar Nuevo Usuario",

      // Configuración Registros de Auditoría
      auditLogsDesc: "Ve cada acción clave realizada en tu cuenta para cumplimiento y transparencia.",
      loggedIn: "Inicio de sesión",
      updatedClient: "Cliente actualizado",
      exportedClientData: "Datos de cliente exportados",
      changedPassword: "Contraseña cambiada",

      // Configuración API
      apiAccessDesc: "Genera tokens API para integraciones de terceros.",
      regenerateToken: "Regenerar Token",
      apiTokenUsage: "Usa tokens API para conectar CRMs, marcadores o fuentes de leads.",

      // Configuración Datos
      dataExportImportDesc: "Exporta o importa tus datos de clientes y pólizas.",
      exportData: "Exportar Datos",
      importData: "Importar Datos",
      exportsEncrypted: "Todas las exportaciones están cifradas y solo pueden ser importadas por un administrador.",

      // Configuración Cumplimiento
      complianceSettings: "Revisa tu configuración de cumplimiento y controles de privacidad.",
      hipaaCompliance: "Cumplimiento HIPAA",
      dataEncryption: "Cifrado de Datos",
      enabled: "Habilitado",
      active: "Activo",
      viewPrivacyPolicy: "Ver Política de Privacidad",

      // Configuración Zona de Peligro
      dangerZoneDesc: "Elimina tu cuenta o restablece todos los datos. Esta acción es irreversible.",
      deleteAccount: "Eliminar Cuenta",
      resetAllData: "Restablecer Todos los Datos",

      // Comunicación Reciente
      noRecentCommunication: "Sin actividad reciente",

      // Página de Inicio de Sesión
      nextGenInsurance: "Retención de Seguros de Nueva Generación",
      emailLabel: "Correo Electrónico",
      passwordLabel: "Contraseña",
      logIn: "Iniciar Sesión",
      forgotPassword: "¿Olvidó su contraseña?",
      noAccount: "¿No tiene una cuenta?",

      // Página de Registro
      createYourAccount: "Crea tu cuenta",
      signingUpAs: "Me estoy registrando como:",
      agency: "Agencia",
      agentRole: "Agente",
      manageAgency: "Administra tu agencia de seguros y tu equipo",
      joinAgencyTeam: "Únete a tu equipo de agencia",
      agencyName: "Nombre de la Agencia",
      yourName: "Tu Nombre",
      numberOfAgents: "Número de Agentes",
      promoCode: "Código Promocional (ej., PILOT2025)",
      passwordMin8: "Contraseña (mín. 8 caracteres)",
      confirmPassword: "Confirmar Contraseña",
      invitedToJoin: "¡Has sido invitado a unirte a una agencia!",
      emailMustMatch: "Correo (debe coincidir con la invitación)",
      invitationToken: "Token de Invitación",
      createAgencyAccount: "Crear Cuenta de Agencia",
      acceptInvitation: "Aceptar Invitación",
      alreadyHaveAccount: "¿Ya tienes una cuenta?",
      needPromoCode: "¿Necesitas un código promocional? Escribe a",
      fmoPartnership: "Asociación FMO",
      fmoContact: "¿Eres un FMO buscando asociarte? Contáctanos en",

      // Página de Recuperar Contraseña
      forgotPasswordTitle: "Olvidé mi Contraseña",
      resetCodeMessage: "Crearemos un código de restablecimiento para tu cuenta.",
      sendCode: "Enviar Código",

      // Página de Perfil del Cliente
      healthConnectedSuccess: "¡Datos de salud conectados exitosamente! Tus datos se sincronizarán en breve.",
      healthConnectionFailed: "Error al conectar datos de salud",
      startsSoon: "Comienza pronto",
      noPhoneOnFile: "No hay número de teléfono registrado para este cliente. Por favor agregue un número primero.",
      confirmCall: "¿Llamar a {{name}} al {{phone}}?\n\nSu teléfono sonará primero, luego lo conectaremos con el cliente.",
      callFailed: "Llamada fallida",
      failedToInitiateCall: "Error al iniciar la llamada. Por favor intente de nuevo.",
      failedToUpdate: "Error al actualizar cliente",
      provideReasonLost: "Por favor proporcione una razón para perder este cliente.",
      lostCustomerPrefix: "--- Cliente Perdido",
      clientMarkedLost: "Cliente marcado como perdido.",
      reactivateConfirm: "¿Reactivar este cliente?",
      clientReactivated: "Cliente reactivado.",
      failedToReactivate: "Error al reactivar",
      failedToCreateTask: "Error al crear tarea",
      activeStatus: "Activo",
      prospectStatus: "Prospecto",
      lostStatus: "Perdido",
      healthConnected: "Salud Conectada",
      syncing: "Sincronizando...",
      sync: "Sincronizar",
      disconnectConfirm: "¿Desconectar?",
      yes: "Sí",
      no: "No",
      connectHealthData: "Conectar Datos de Salud",
      markAsLost: "Marcar como Perdido",
      reactivate: "Reactivar",
      markClientLostTitle: "¿Marcar Cliente como Perdido?",
      markClientLostConfirm: "¿Estás seguro de que quieres marcar a {{name}} como perdido?",
      canReactivateLater: "Esta acción cambiará el estado del cliente. Puedes reactivarlo más tarde si es necesario.",
      reasonForLosing: "Razón para perder este cliente",
      reasonPlaceholder: "ej., Cambió a la competencia, se mudó, preocupaciones de costo...",
      yesMarkAsLost: "Sí, Marcar como Perdido",
      callToDiscussPlaceholder: "Llamar para discutir renovación de póliza...",
      additionalDetailsPlaceholder: "Detalles adicionales...",

      // Tarjeta de Detalles del Cliente
      healthDataConnected: "Datos de Salud Conectados",
      planType: "Tipo de Plan",
      lastSynced: "Última Sincronización",
      prescriptions: "Recetas",
      medicationsCount: "({{count}} medicamentos)",
      noPrescriptionsFound: "No se encontraron recetas. Haga clic en Sincronizar para obtener datos.",
      connectMedicareBlueButton: "Conectar Medicare Blue Button para importar recetas",
      medication: "Medicamento",
      lastFilled: "Última Recarga",
      qty: "Cant",
      days: "Días",
      cost: "Costo",
      pharmacy: "Farmacia",
      soa: "SOA",
      ptc: "PTC",
      enrollment: "Inscripción",
      other: "Otro",
      uploading: "Subiendo…",
      document: "Documento",

      // Modal de Edición de Cliente
      editClientDetails: "Editar Detalles del Cliente",
      autoFormatsPhone: "Auto-formatea a +1 (XXX) XXX-XXXX",
      city: "Ciudad",
      state: "Estado",
      zip: "Código Postal",
      autoTranslatesSmsEmail: "(Auto-traduce SMS/Correo)",
      englishFlag: "Inglés",
      spanishFlag: "Español",
      soaOnFile: "SOA en Archivo (verdadero/falso)",
      soaSigned: "SOA Firmado (AAAA-MM-DD)",
      ptcOnFile: "Permiso de Contacto en Archivo (verdadero/falso)",
      ptcSigned: "PTC Firmado (AAAA-MM-DD)",
      enrollmentOnFile: "Formulario de Inscripción en Archivo (verdadero/falso)",

      // Formulario Completo de Cliente
      phonePlaceholder: "(555) 123-4567",
      emailPlaceholderForm: "nombre@ejemplo.com",
      addressPlaceholder: "123 Calle Principal, Ciudad, ST 12345",
      dob: "F. Nac.",
      carrierPlaceholder: "UHC / Humana / Aetna…",
      planNamePlaceholder: "Nombre / código del plan",
      primaryCarePlaceholder: "Dr. Juan Pérez",
      specialistsPlaceholder: "Dra. Ana Especialista, Dr. Carlos Ortopeda",
      medicationsPlaceholder: "Aspirina, Lisinopril",
      soaOnFileLabel: "SOA en archivo",
      soaSignedLabel: "SOA firmado (fecha)",
      ptcOnFileLabel: "Permiso de contacto en archivo",
      ptcSignedLabel: "PTC firmado (fecha)",
      enrollmentOnFileLabel: "Formulario de inscripción en archivo",
      anyDetailsPlaceholder: "Cualquier detalle…",
      autoTranslateNote: "Cuando envíes SMS o correo a este cliente, se traducirá automáticamente a su idioma preferido.",

      // Registro de Contacto
      enterSubjectOrNote: "Ingresa un asunto o nota.",
      failedToSaveOutreach: "Error al guardar el contacto.",
      deleteEntryConfirm: "¿Eliminar esta entrada?",
      outreachLog: "Registro de Contactos",
      outreachLogSubtitle: "Llamadas, mensajes, correos — todo en un lugar.",
      subjectOptional: "Asunto (opcional)",
      addEntry: "Agregar Entrada",
      noOutreachYet: "Sin contactos aún.",
      when: "Cuándo",
      type: "Tipo",
      note: "Nota",

      // Cajón de Comunicaciones
      allCommunication: "Toda la Comunicación",
      texts: "Mensajes",
      emails: "Correos",
      calls: "Llamadas",
      appointments: "Citas",
      noEntriesYet: "Sin entradas aún.",
      clientToYou: "Cliente → Tú",
      youToClient: "Tú → Cliente",
      subjectLabel: "Asunto:",
      previewLabel: "Vista previa:",
      duration: "Duración",

      // Hilo de Mensajes
      smsText: "Mensaje SMS",
      smsSentSuccess: "¡SMS enviado exitosamente!",
      emailSentSuccess: "¡Correo enviado exitosamente!",
      failedToSend: "Error al enviar",
      unread: "sin leer",

      // Sección de Integraciones
      integrationsTitle: "Integraciones",
      integrationsDesc: "Conecta tu correo y calendario para enviar correos profesionales desde tu cuenta y sincronizar citas.",
      googleAccount: "Cuenta de Google",
      gmailCalendar: "Gmail + Google Calendar",
      connected: "Conectado",
      notConnected: "No Conectado",
      emailSending: "Envío de Correos",
      sendEmailsFromGmail: "Envía correos a clientes desde tu dirección de Gmail",
      calendarSync: "Sincronización de Calendario",
      autoSyncAppointments: "Sincroniza automáticamente citas y tareas",
      connectedOn: "Conectado el {{date}}",
      disconnect: "Desconectar",
      connectGoogleAccount: "Conectar Cuenta de Google",
      microsoftAccount: "Cuenta de Microsoft",
      outlookM365: "Outlook + Microsoft 365",
      sendEmailsFromOutlook: "Envía correos a clientes desde tu dirección de Outlook",
      connectMicrosoftAccount: "Conectar Cuenta de Microsoft",
      whyConnectEmail: "¿Por qué conectar tu correo?",
      emailBenefit1: "Los correos vienen de TU dirección (más confiable para clientes)",
      emailBenefit2: "Las respuestas van directamente a tu bandeja de entrada",
      emailBenefit3: "Traducción automática al español para comunicaciones con clientes",
      emailBenefit4: "Apariencia profesional y mejor entrega",

      // Texto de duración
      day: "día",
      days: "días",
      month: "mes",
      months: "meses",
      year: "año",
      years: "años",

      // Panel de Fundador
      founderDashboard: "Panel de Fundador",
      trackPilotPerformance: "Rastrea el rendimiento del programa piloto, adopción de agentes y métricas del sistema",
      totalAgents: "Total de Agentes",
      activeAgentsCount: "{{active}} activos, {{inactive}} inactivos",
      communications: "Comunicaciones",
      acrossAllChannels: "En todos los canales",
      taskCompletion: "Finalización de Tareas",
      tasksOfTotal: "{{completed}} de {{total}} tareas",
      agentPerformance: "Rendimiento de Agentes",
      topPerformers: "Mejores Agentes",
      communicationActivity: "Actividad de Comunicación",
      retentionByAgent: "Retención por Agente",
      systemAdoption: "Adopción del Sistema",
      csvImportsPerformed: "Importaciones CSV Realizadas",
      bulkClientUploads: "Cargas masivas de clientes por agentes",
      avgLoginsPerAgent: "Promedio de Inicios de Sesión por Agente",
      activeEngagement7Days: "Participación activa durante 7 días",
      noAgentData: "No hay datos de agentes disponibles",
      noPerformersData: "No hay datos de rendimiento disponibles",
      noCommunicationData: "No hay datos de comunicación disponibles",
      noRetentionData: "No hay datos de retención disponibles",
      loadingFounderMetrics: "Cargando métricas de fundador...",
      accessDeniedFounder: "Acceso denegado - Las métricas de fundador solo están disponibles para usuarios FMO/Admin",
      failedToFetchFounder: "Error al obtener métricas de fundador",
      comms: "comunicaciones",
      retention: "retención",
      appointment: "Cita",

      // Gráficos de Retención
      retentionTrend: "Tendencia de Retención",
      churnBreakdown: "Desglose de Bajas",
      retentionTrendDemo: "Tendencia de Retención (Demo)",
      churnBreakdownDemo: "Desglose de Bajas (Demo)",
      sampleDataOnly: "Datos de muestra – análisis real próximamente",

      // Búsqueda Rápida
      quickClientLookup: "Búsqueda Rápida de Cliente",
      searchByName: "Buscar por nombre...",
      noClientsFound: "No se encontraron clientes",

      // Modal de Baja
      logClientChurn: "Registrar Baja de Cliente",
      hasLeftPlan: "{{name}} ha dejado el plan",
      whenDidTheyLeave: "¿Cuándo se fueron?",
      primaryReasonForLeaving: "Razón Principal de la Baja",
      newCarrier: "Nueva Aseguradora",
      newPlan: "Nuevo Plan",
      couldThisBeenPrevented: "¿Se pudo haber prevenido?",
      yesPreventable: "Sí, prevenible",
      noUnavoidable: "No, inevitable",
      whatCouldPrevented: "¿Qué pudo haberlo prevenido?",
      earlyOutreach: "Contacto más temprano",
      betterPlanMatch: "Mejor recomendación de plan",
      costAssistance: "Ayuda con costo/inscripción LIS",
      providerNetwork: "Resolución de problema de red/proveedor",
      education: "Mejor educación sobre el plan",
      relationshipBuilding: "Construcción de relación más fuerte",
      warningSignsQuestion: "¿Qué señales de advertencia notaste? (ayuda a entrenar la IA)",
      additionalNotes: "Notas Adicionales",
      anyContextWhy: "Cualquier contexto adicional sobre por qué se fueron...",
      logChurn: "Registrar Baja",
      logging: "Registrando...",
      pleaseSelectPrimaryReason: "Por favor seleccione una razón principal",
      costIssues: "Problemas de Costo",
      coverageIssues: "Problemas de Cobertura",
      serviceIssues: "Problemas de Servicio",
      lifeEvents: "Eventos de Vida",
      competitive: "Competencia",
      complainedAboutCopays: "ej., Se quejó de los copagos",
      egHumana: "ej., Humana",
      planName: "Nombre del plan",
      maMedicareAdvantage: "MA (Medicare Advantage)",
      maPd: "MA-PD",
      dsnp: "DSNP",
      pdpPartD: "PDP (Parte D)",
      medigap: "Medigap",
      unknown: "Desconocido",

      // Página de Automatizaciones
      automationsWorkflows: "Automatizaciones y Flujos de Trabajo",
      newWorkflow: "Nuevo Flujo de Trabajo",
      help: "Ayuda",
      myAutomations: "Mis Automatizaciones",
      birthdayTextEmail: "Texto/Correo de Cumpleaños",
      sendsHappyBirthday: "Envía mensaje de feliz cumpleaños a los clientes",
      policyAnniversary: "Aniversario de Póliza",
      annualCheckInReminder: "Recordatorio anual en fecha de póliza",
      monthlyNewsletter: "Boletín Mensual",
      sendsMonthlyUpdates: "Envía boletín mensual de consejos de salud",
      aepReminder: "Recordatorio AEP",
      octAepReminder: "Recordatorio AEP del 1 de octubre para clientes elegibles",
      oepFollowUp: "Seguimiento OEP",
      janOepCheckin: "Secuencia de seguimiento OEP Ene-Mar",
      renewalReminder: "Recordatorio de Renovación",
      daysBeforeRenewal: "Aviso 30 días antes de la renovación",
      activityFeed: "Feed de Actividad",
      analytics: "Análisis",
      automationsSent: "Automatizaciones Enviadas",
      openRate: "Tasa de Apertura",
      clickRate: "Tasa de Clics",
      clientsReached: "Clientes Alcanzados",
      templatesTitle: "Plantillas",
      createNew: "Crear Nueva",
      personalization: "Personalización",
      planTypeVar: "Tipo de Plan",
      birthdayTemplate: "Cumpleaños",
      anniversaryTemplate: "Aniversario",
      aepTemplate: "AEP",
      oepTemplate: "OEP",
      proIrpAssistant: "Asistente Pro IRP",
      askAboutAutomations: "Pregunta sobre automatizaciones, mejores prácticas, u obtén ayuda configurando flujos de trabajo...",
      typeQuestion: "Escribe una pregunta...",
      automationsComingSoon: "¡Motor de automatizaciones próximamente! Configura deseos de cumpleaños, recordatorios de aniversario, alcance AEP/OEP y más.",

      // Asistente AEP
      aepWizardTitle: "Asistente AEP",
      aepStartsIn: "AEP comienza en:",
      daysLabel: "días",
      hoursLabel: "horas",
      minsLabel: "mins",
      eventMode: "Modo Evento",
      letsWinAEP: "¡Ganemos AEP!",
      sendPreAepBlast: "Enviar Mensaje Pre-AEP",
      openBooking: "Abrir Reservas",
      createCallList: "Crear Lista de Llamadas",
      outreachAutomations: "Automatizaciones de Contacto",
      templates: "Plantillas",
      preAepCheckin: "Verificación Pre-AEP",
      aepOpenEnrollment: "Inscripción Abierta AEP",
      planComparison: "Comparación de Planes",
      benefitsReview: "Revisión de Beneficios",
      countdownList: "Lista de Cuenta Regresiva",
      clientsToContactBeforeAep: "Clientes para contactar antes de AEP",
      allClientsContacted: "¡Todos los clientes contactados!",
      contacted: "Contactado",
      needsContact: "Necesita Contacto",
      noClientsToContact: "No hay clientes para contactar",
      aepGoals: "Metas AEP",
      setGoal: "Establecer Meta",
      enrollments: "Inscripciones",
      appointments: "Citas",
      currentPeriod: "Período Actual",
      preAep: "Pre-AEP",
      aepActive: "AEP Activo",
      postAep: "Post-AEP",
      daysUntilAepEnds: "días hasta que termine AEP",
      aepHasEnded: "AEP ha terminado",
      quickActions: "Acciones Rápidas",
      scheduleAepEvent: "Programar Evento AEP",
      sendMassEmail: "Enviar Correo Masivo",
      downloadMaterials: "Descargar Materiales",
      viewReports: "Ver Reportes",
      aepTips: "Consejos AEP",
      tipContactHighRisk: "Contacta primero a clientes de alto riesgo",
      tipReviewPlanChanges: "Revisa cambios de planes de cada aseguradora",
      tipPrepareComparisons: "Prepara hojas de comparación de planes",
      tipScheduleFollowups: "Programa seguimientos para clientes indecisos",
      aepResources: "Recursos AEP",
      carrierUpdates: "Actualizaciones de Aseguradoras",
      viewLatestChanges: "Ver últimos cambios de planes",
      complianceGuide: "Guía de Cumplimiento",
      stayCompliant: "Mantente en cumplimiento durante AEP",
      marketingMaterials: "Materiales de Marketing",
      downloadFlyers: "Descargar volantes y folletos",
      trainingVideos: "Videos de Capacitación",
      watchTutorials: "Ver tutoriales de AEP",
      recentAepActivity: "Actividad AEP Reciente",
      noRecentAepActivity: "Sin actividad AEP reciente",
      scheduledAepEvent: "Evento AEP programado",
      sentAepEmail: "Correo AEP enviado a",
      completedAepCall: "Llamada AEP completada con",

      // Centro OEP
      oepRetentionHub: "Centro de Retención OEP",
      retentionMode: "Modo Retención",
      keepEveryWin: "Mantén Cada Victoria",
      sendJan1Blast: "Enviar Mensaje del 1 de Enero",
      oepCohort: "Cohorte OEP",
      followUpsSent: "Seguimientos Enviados",
      churn: "Bajas",
      oepAutomations: "Automatizaciones OEP",
      jan1Welcome: "Bienvenida 1 de Enero",
      benefitReminder: "Recordatorio de Beneficios",
      satisfactionCheck: "Verificación de Satisfacción",
      oepDeadline: "Fecha Límite OEP",
      retentionList: "Lista de Retención",
      clientsToRetainDuringOep: "Clientes a retener durante OEP",
      allClientsRetained: "¡Todos los clientes retenidos!",
      retained: "Retenido",
      atRisk: "En Riesgo",
      noClientsAtRisk: "No hay clientes en riesgo",
      oepGoals: "Metas OEP",
      retentionGoal: "Meta de Retención",
      checkIns: "Verificaciones",
      currentOepPeriod: "Período OEP Actual",
      oepActive: "OEP Activo",
      preOep: "Pre-OEP",
      postOep: "Post-OEP",
      daysUntilOepEnds: "días hasta que termine OEP",
      oepHasEnded: "OEP ha terminado",
      oepQuickActions: "Acciones Rápidas",
      scheduleCheckIn: "Programar Verificación",
      sendRetentionEmail: "Enviar Correo de Retención",
      reviewAtRisk: "Revisar En Riesgo",
      oepTips: "Consejos OEP",
      tipReachOutEarly: "Contacta temprano a clientes en riesgo",
      tipAddressConcerns: "Aborda preocupaciones antes de que cambien",
      tipHighlightBenefits: "Destaca beneficios que pueden haber olvidado",
      tipDocumentInteractions: "Documenta todas las interacciones con clientes",
      oepResources: "Recursos OEP",
      retentionScripts: "Guiones de Retención",
      provenTalkingPoints: "Puntos de conversación probados",
      benefitHighlights: "Puntos Destacados de Beneficios",
      keyBenefitsbyPlan: "Beneficios clave por plan",
      competitorInfo: "Información de Competencia",
      knowCompetition: "Conoce a la competencia",
      recentOepActivity: "Actividad OEP Reciente",
      noRecentOepActivity: "Sin actividad OEP reciente",
      completedRetentionCall: "Llamada de retención completada",
      sentFollowUpTo: "Seguimiento enviado a",
      scheduledCheckInWith: "Verificación programada con",

      // Cadenas adicionales de Automatizaciones
      active: "Activo",
      inactive: "Inactivo",
      delivered: "Entregado",
      opened: "Abierto",
      failed: "Fallido",
      resend: "Reenviar",
      exportActivityCsv: "Exportar Actividad (CSV)",
      searchTemplates: "Buscar plantillas...",
      preview: "Vista previa",
      insert: "Insertar",
      testSend: "Envío de Prueba",
      insertIntoWorkflow: "Insertar en Flujo de Trabajo",
      createNewWorkflow: "Crear Nuevo Flujo de Trabajo",
      workflowName: "Nombre",
      triggers: "Disparadores",
      actionsLabel: "Acción(es)",
      actionPlaceholder: "ej., Enviar correo, texto, asignar tarea (usa {ClientName}, {AgentName} etc. para personalización)",
      createWorkflow: "Crear Flujo de Trabajo",
      needInspiration: "¿Necesitas inspiración? ¡Revisa el panel de Plantillas para mensajes listos para usar!",
      askMeAnything: "Pregúntame lo que sea...",
      automationHelpTips: "Ayuda y Consejos de Automatización",
      toggleAutomations: "Activa/desactiva automatizaciones en cualquier momento con los interruptores.",
      addWorkflows: "Agrega flujos de trabajo para disparadores personalizados, mensajes o secuencias de múltiples pasos.",
      usePersonalizationTags: "Usa etiquetas de personalización en todas las plantillas para un alcance único y personalizado.",
      previewAndTestSend: "Previsualiza y envía pruebas de cada mensaje antes de activarlo.",
      aiAssistantCanHelp: "El Asistente IA puede ayudarte a redactar, explicar u optimizar cualquier mensaje o automatización.",
      checkActivityFeed: "Revisa el Feed de Actividad para el estado de entrega y reenvío rápido si algo falla.",
      seeStatsInAnalytics: "Mira tus estadísticas y tasas de respuesta en la sección de Análisis abajo.",
      clientName: "Nombre del Cliente",
      agentName: "Nombre del Agente",
      agentPhone: "Teléfono del Agente",
      policyYear: "Año de Póliza",
      replyRate: "Tasa de Respuesta",
      bounces: "Rebotes",
      failedSends: "Envíos Fallidos",
      askAi: "Preguntar IA",
      personalizedForEachClient: "Usa estas etiquetas en cualquier mensaje—cada una se reemplazará automáticamente para cada cliente.",

      // Cadenas adicionales del Asistente AEP
      loadingPlaybook: "Cargando el manual...",
      enterWizard: "Entrar al Asistente",
      focusedOutreach: "Alcance enfocado. Reservas más rápidas. Prioridades claras. Haz que cada contacto cuente.",
      preAepOutreach: "Alcance Pre-AEP",
      bookingGoal: "Meta de Reservas",
      riskCoverage: "Cobertura de Riesgo",
      repliesHandled: "Respuestas Manejadas",
      preAep60Days: "Pre-AEP: 60 días",
      preAep30Days: "Pre-AEP: 30 días",
      preAep14Days: "Pre-AEP: 14 días",
      preAep7Days: "Pre-AEP: 7 días",
      preAep3Days: "Pre-AEP: 3 días",
      preAep1Day: "Pre-AEP: 1 día",
      anocExplainer: "Explicador ANOC",
      bookingNudges: "Recordatorios de Reserva",
      voicemailDropUi: "Mensaje de Voz (solo UI)",
      requireApproval: "Requiere Aprobación",
      searchTemplatesPlaceholder: "Buscar plantillas...",
      featured: "Destacado",
      aepWizardHelper: "Ayudante del Asistente AEP",
      askMeToDraft: "Pídeme que redacte, optimice o resuma…",
      addContact: "Agregar Contacto",
      sendDrip: "Enviar Goteo",
      noContactsYet: "Sin contactos aún. Haz clic en Agregar Contacto para comenzar tu Lista de Cuenta Regresiva.",
      addCountdownContact: "Agregar Contacto de Cuenta Regresiva",
      editCountdownContact: "Editar Contacto de Cuenta Regresiva",
      zip: "Código Postal",
      county: "Condado",
      dob: "Fecha de Nacimiento",
      languageLabel: "Idioma",
      sourceLabel: "Fuente",
      statusLabel: "Estado",
      permissionToContact: "Permiso para Contactar",
      monthlyNewsletterLabel: "Boletín Mensual",
      outreachPlan: "Plan de Alcance",
      twoMonths: "2 Meses",
      oneMonth: "1 Mes",
      twoWeeks: "2 Semanas",
      oneWeek: "1 Semana",
      aepLive: "AEP en Vivo",
      saveChanges: "Guardar Cambios",
      usingAepWizard: "Usando el Asistente AEP",
      useQuickActions: "Usa Acciones Rápidas para enviar mensajes pre-AEP, crear listas de llamadas y abrir tu agenda.",
      automationsControlDrips: "Las automatizaciones controlan tus goteos pre-AEP (60/30/14/7/3/1 días), explicadores ANOC y recordatorios de reserva.",
      templatesPersonalize: "Las plantillas están listas para personalizar con etiquetas de combinación.",
      countdownListStores: "La Lista de Cuenta Regresiva almacena prospectos durante todo el año que deben esperar hasta AEP—agrega, rastrea el estado y envía goteos automáticamente.",
      aiHelperCanDraft: "El Ayudante IA puede redactar textos, líneas de asunto y frases más seguras para cumplimiento (demo).",
      personalizationExample: "Ejemplo de Personalización",
      mergeTags: "Etiquetas de Combinación",
      openAiHelper: "Abrir Ayudante IA",
      exportCsv: "Exportar (CSV)",
      newStatus: "Nuevo",
      warmStatus: "Cálido",
      scheduledStatus: "Programado",
      enrolledStatus: "Inscrito",
      notInterestedStatus: "No Interesado",
      eventSource: "Evento",
      referralSource: "Referido",
      inboundSource: "Entrante",
      otherSource: "Otro",
      englishLang: "Inglés",
      spanishLang: "Español",
      otherLang: "Otro",

      // Cadenas adicionales del Centro OEP
      oepSeasonActive: "Activo",
      oepSeasonUpcoming: "Próximo",
      stopChurnBeforeStarts: "Bienvenida. Seguimiento. Consolida la relación. Detén las bajas antes de que comiencen.",
      openServiceDesk: "Abrir Mesa de Servicio",
      referralToolkit: "Kit de Referidos",
      jan1Congrats: "1 Ene: ¿Felicitaciones / Tarjetas Recibidas?",
      feb1FirstMonth: "1 Feb: Seguimiento del Primer Mes Completo",
      mar1FollowUpReferrals: "1 Mar: Seguimiento y Referidos",
      monthlyNewslettersCohort: "Boletines Mensuales para el Cohorte",
      oepRetentionHelper: "Ayudante de Retención OEP",
      askForCheckin: "Pide un seguimiento del 1 de Feb, rescate de baja o texto de referido…",
      addOepClient: "Agregar Cliente OEP",
      editOepClient: "Editar Cliente OEP",
      effectiveDateLabel: "Fecha de Vigencia",
      firstTimeWithAgent: "Primera Vez con el Agente",
      sendJan1: "Enviar 1 Ene",
      sendFeb1: "Enviar 1 Feb",
      sendMar1: "Enviar 1 Mar",
      noClientsInOepCohort: "No hay clientes en el cohorte OEP aún. Agrega uno o sincroniza desde tu lista de Clientes.",
      cohortWindowNote: "Los clientes agregados aquí se filtran al cohorte OEP si su fecha de vigencia está entre {{start}} y {{end}} y es su primera vez con el agente.",
      activeStatus: "Activo",
      switchedStatus: "Cambiado",
      cancelledStatus: "Cancelado",
      yesLabel: "Sí",
      noLabel: "No"
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

export default i18n;
